import { timestampDate } from '@bufbuild/protobuf/wkt';
import {
  CateringOrder,
  CateringOrderDetail,
  Destination,
  LineItem,
} from '@bufteam/cfacorp_rex-catering.bufbuild_es/cfa/rex/catering/v1/catering_order_pb';
import { TZDateMini } from '@date-fns/tz';
import { format } from 'date-fns';
import * as z from 'zod';

import ooeConstants from '../constants';
import { capitalizeFirstLetter, formatDeliveryAddress, titleCase } from '../util/format';
import normalizePhone from '../util/normalizePhone';
import { DashboardOrder, DashboardOrderLineItem } from './schemas';
import { CurrentTierName } from './types';
import { getCfaLoyalty } from './utils';

export const cateringToDashboardOrder = z.transform<CateringOrder, DashboardOrder>((o) => {
  const date = o.promiseTime ? new TZDateMini(timestampDate(o.promiseTime), '+00:00') : undefined;
  const dateTime = date ? format(date, ooeConstants.DATE_TIME_FORMAT.dateTime) : '';

  return {
    id: o.orderId || o.transId,
    cfaId: o.customerId,
    transId: o.transId,
    locationNumber: o.locationId,
    status: o.status,
    subTotalAmount: o.subTotal,
    taxAmount: o.tax,
    payment: {
      paymentType: o.paymentType,
    },
    destination: titleCase(mapCateringDestination(o.destination)),
    cfaLoyalty: getCfaLoyalty(o.currentTierName as CurrentTierName),
    promiseDateTime: dateTime,
    time: date ? format(date, ooeConstants.DATE_TIME_FORMAT.time) : '',
    utcTime: dateTime,
    clientId: o.clientId,
    givenName: capitalizeFirstLetter(o.givenName),
    familyName: capitalizeFirstLetter(o.lastName),
  };
});

const mapCateringDestination = (destination: Destination) => {
  switch (destination) {
    case Destination.CARRY_OUT:
      return 'carry out';
    case Destination.PICKUP:
      return 'pickup';
    case Destination.CATERING_DELIVERY:
      return 'delivery';
    case Destination.DELIVERY:
      return 'cfa delivery';
    case Destination.CURBSIDE:
      return 'curbside';
    case Destination.DINE_IN:
      return 'dine in';
    case Destination.DRIVE_THRU:
      return 'drive thru';
    case Destination.ON_DEMAND:
      return 'on demand';
    default:
      return 'unspecified';
  }
};

const mapProtobufLineItem = (lineItem: LineItem): DashboardOrderLineItem => ({
  itemTag: lineItem.itemTag,
  name: lineItem.name,
  quantity: lineItem.quantity,
  retailPrice: lineItem.retailPrice,
  subItems: lineItem.subItems.length > 0 ? lineItem.subItems.map(mapProtobufLineItem) : undefined,
});

export const cateringToDashboardDetail = z.transform<CateringOrderDetail, Partial<DashboardOrder>>((o) => ({
  deliveryAddress:
    Object.keys(o.deliveryAddress).length > 0 ? formatDeliveryAddress(o.deliveryAddress) : undefined,
  phone: o.phone ? normalizePhone(o.phone) : undefined,
  taxAmount: o.taxAmount,
  subTotalAmount: o.subTotalAmount,
  totalAmount: o.tranTotal,
  specialInstructions: o.specialInstructions || undefined,
  lineItems: o.lineItems.map(mapProtobufLineItem),
}));
